import { initNumberField } from '@general/helpers';
import { addItemToCart, afterCartChangeType, changeCart, removeFromCart } from '@services/cart';
import { debounce } from 'lodash';
import { closeModal, openModal } from './modals';

// Store listener reference for cleanup
let cartEventListenerAttached = false;

/**
 * Finds the minicart element within the specified root element.
 *
 * @param {Document|HTMLElement} [root=document] - The root element to search within.
 * @returns {HTMLElement|null} The minicart element if found, otherwise null.
 */
const findMinicart = (root = document) => root.querySelector('#MinicartDrawer');

/**
 * Initializes the minicart by setting up event listeners and initializing line items.
 */
const initMinicart = () => {
  const minicart = findMinicart();
  if (!minicart) return;

  const form = minicart.querySelector('form');
  if (form && !form.dataset.submitGuarded) {
    form.dataset.submitGuarded = 'true';
    let checkoutClicked = false;
    const checkoutBtn = form.querySelector('[name="checkout"]');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => {
        checkoutClicked = true;
      });
    }
    form.addEventListener('submit', (e) => {
      if (!checkoutClicked) {
        e.preventDefault();
      }
      checkoutClicked = false;
    });
  }

  const productRows = minicart.querySelectorAll('.cart-line__wrapper[data-group]:not([data-group=""])');
  const grouped = new Set();

  productRows.forEach((row) => {
    const groupId = row.getAttribute('data-group');
    if (!grouped.has(groupId)) {
      const groupRows = Array.from(
        minicart.querySelectorAll(`.cart-line__wrapper[data-group="${groupId}"]:not([data-grouped="true"])`),
      );
      if (groupRows.length > 0) {
        const groupDiv = document.createElement('div');
        groupDiv.classList.add('cart-line__products-group');
        groupRows[0].parentNode.insertBefore(groupDiv, groupRows[0]);
        groupRows.forEach((grRow) => {
          groupDiv.appendChild(grRow);
          grRow.setAttribute('data-grouped', 'true');
        });
      }
      grouped.add(groupId);
    }
  });

  minicart.querySelectorAll('.cart-line__products-group').forEach((group) => {
    const hasGiftwrap = group.querySelector('.cart-line__wrapper[data-giftwrap="true"]');
    const opener = group.querySelector('.cart-line__giftwrap');
    if (opener) {
      if (!hasGiftwrap) {
        opener.classList.add('visible');
      } else {
        opener.classList.remove('visible');
      }
    }
  });

  const privacyCheckbox = minicart.querySelector('.privacy-checkbox__container');
  if (privacyCheckbox && typeof window.dAgency.initPrivacyCheckbox == 'function')
    window.dAgency.initPrivacyCheckbox(privacyCheckbox);
  minicart.querySelectorAll('.cart-line__wrapper[data-key]').forEach((line) =>
    initLine({
      line,
      beforeFetch: () => {
        minicart.classList.add('loading');
      },
      afterFetch: () => {
        updateMinicart();
      },
    }),
  );
};

/**
 * Updates the minicart content by fetching the latest data and reinitializing the minicart.
 */
const updateMinicart = async () => {
  const minicart = findMinicart();
  if (!minicart) return;
  minicart.classList.add('loading');
  await fetch(minicart.dataset.refreshUrl)
    .then((res) => res.text())
    .then((data) => {
      const minicartSection = new DOMParser().parseFromString(data, 'text/html');
      const fetchMinicart = findMinicart(minicartSection);
      const privacyInput = minicart.querySelector('.privacy-checkbox__container input[type="checkbox"]');
      const wasChecked = privacyInput ? privacyInput.checked : false;
      if (fetchMinicart) minicart.innerHTML = fetchMinicart.innerHTML;
      minicart
        .querySelectorAll('.modal__backdrop , .modal__close')
        .forEach((closer) => closer.addEventListener('click', () => closeModal(minicart)));
      initMinicart();
      if (wasChecked) {
        const newPrivacyInput = minicart.querySelector('.privacy-checkbox__container input[type="checkbox"]');
        if (newPrivacyInput) {
          newPrivacyInput.checked = true;
          newPrivacyInput.dispatchEvent(new Event('change'));
        }
      }
    });
  minicart.classList.remove('loading');
};

/**
 * Initializes a line item in the minicart by setting up event listeners for
 * quantity changes and item removal.
 * @param {Object} options - Options for initializing the line item.
 * @param {HTMLElement} options.line - The line item element.
 * @param {Function} [options.beforeFetch] - Function to call before sending the request.
 * @param {Function} [options.afterFetch] - Function to call after successfully changing the cart item.
 */
const initLine = ({ line, beforeFetch, afterFetch }) => {
  if (!line) return;
  const minusBtn = line.querySelector('.cart-line__quantity-btn--minus');
  const plusBtn = line.querySelector('.cart-line__quantity-btn--plus');
  const field = line.querySelector('.cart-line__quantity-value');

  if (minusBtn && plusBtn && field) {
    initNumberField({
      plusBtn,
      minusBtn,
      field,
    });

    field.addEventListener(
      'change',
      debounce(() => {
        changeCart({
          key: line.dataset.key,
          quantity: parseInt(field.value),
          callbacks: {
            beforeFetch: beforeFetch,
            afterFetch: afterFetch,
          },
        });
      }, 300),
    );
  }

  const remove = line.querySelector('.cart-line__remove');
  if (remove) {
    remove.addEventListener('click', (e) => {
      e.preventDefault();
      const group = line.closest('.cart-line__products-group');
      const keys = new Set([line.dataset.key]);

      // `remove` is the wrapper div, the `giftwrap` class is on the inner link: check the line instead
      if (line.dataset.giftwrap !== 'true') {
        const customization = group?.querySelector('.cart-line__wrapper[data-customization="true"]');
        if (customization) keys.add(customization.dataset.key);
        const giftwrap = group?.querySelector('.cart-line__wrapper[data-giftwrap="true"]');
        if (giftwrap) keys.add(giftwrap.dataset.key);
      } else {
        const opener = group?.querySelector('.giftwrap-opener');
        if (opener) opener.classList.add('visible');
      }

      // change.js instead of update.js so Shopify emits `product_removed_from_cart` for tracking
      removeFromCart({
        keys: [...keys],
        callbacks: {
          beforeFetch,
          afterFetch,
        },
      });
    });
  }

  const giftwrapOpener = line.querySelector('.giftwrap-opener');
  if (giftwrapOpener && line.classList.contains('minicart-item')) {
    giftwrapOpener.addEventListener('click', () => {
      const groupId = line.getAttribute('data-group');
      const drawer = document.querySelector(`#gift__drawer--${groupId}`);
      if (drawer) {
        drawer.classList.add('open');

        const closeBtn = drawer.querySelector('.gift__drawer__close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            drawer.classList.remove('open');
          });
        }

        const products = drawer.querySelectorAll('.gift__drawer__content__product');
        const message = drawer.querySelector('.gift__drawer__content__message');
        const actions = drawer.querySelector('.gift__drawer__footer');
        const button = actions ? actions.querySelector('button[data-group]') : null;

        products.forEach((product) => {
          product.addEventListener('click', () => {
            products.forEach((p) => p.classList.remove('selected'));
            product.classList.add('selected');
            if (message) message.classList.add('open');
            if (button) {
              button.removeAttribute('disabled');
              button.setAttribute('data-item', product.getAttribute('data-item'));
            }
          });
        });

        if (button) {
          button.addEventListener('click', () => {
            const variantId = button.getAttribute('data-item');
            const giftMessage = drawer.querySelector('textarea[name="gift-message"]')?.value || '';
            if (variantId) {
              addItemToCart({
                id: variantId,
                quantity: 1,
                properties: {
                  _UniqueId: groupId,
                  'gift-message': giftMessage,
                },
                callbacks: {
                  beforeFetch: () => drawer.classList.add('loading'),
                  afterFetch: () => {
                    drawer.classList.remove('loading');
                    drawer.classList.remove('open');
                    updateMinicart();
                  },
                },
              });
            }
          });
        }
      }
    });
  }
};

/**
 * Opens the minicart, optionally loading its content before opening.
 *
 * @param {boolean} [loadBeforeOpen=false] - Whether to load the minicart content before opening it.
 */
const openMinicart = async (loadBeforeOpen = false) => {
  if (loadBeforeOpen) await updateMinicart();
  openModal(findMinicart());
};

/**
 * Closes the minicart.
 */
const closeMinicart = () => closeModal(findMinicart());

/**
 * Handler function for cart update events
 * Defined at module scope to allow proper cleanup
 */
const handleMinicartUpdate = () => {
  const minicart = findMinicart();

  // Only update if minicart exists and is not already loading
  if (minicart && !minicart.classList.contains('loading')) {
    updateMinicart();
  }
};

/**
 * Initializes global cart event listener
 * Uses flag to prevent multiple attachments
 */
const initCartEventListener = () => {
  // Prevent multiple listener attachments
  if (cartEventListenerAttached) {
    console.log('[Minicart] Cart event listener already attached');
    return;
  }

  document.addEventListener(afterCartChangeType, handleMinicartUpdate);
  cartEventListenerAttached = true;

  console.log('[Minicart] Cart event listener initialized');
};

/**
 * Removes the global cart event listener
 * Used for cleanup in dynamic scenarios
 */
const removeCartEventListener = () => {
  if (cartEventListenerAttached) {
    document.removeEventListener(afterCartChangeType, handleMinicartUpdate);
    cartEventListenerAttached = false;
    console.log('[Minicart] Cart event listener removed');
  }
};

// Initialize event listener when module loads
initCartEventListener();

export { closeMinicart, initLine, initMinicart, openMinicart, removeCartEventListener };
